/**
 * Enum values shared by the API (Prisma) and the SPA. These strings are persisted,
 * so renaming one is a migration, not a refactor.
 */

/**
 * What a user is *allowed to do*.
 *
 * Deliberately NOT a rank ladder. IT and HR are peers with different scopes: IT owns
 * the hardware (register, assign, return, repair), HR owns the people (onboard,
 * exit, clearance). Neither is "more" than the other, and an "at least HR" check
 * would silently hand IT the power to delete an employee record - or the reverse.
 *
 * So every permission below is a named predicate rather than a comparison. This is
 * the same lesson the PMS learned with canManageKraLibrary: the moment a permission
 * is a fact about scope rather than seniority, a ladder cannot express it.
 */
export const ROLES = ['EMPLOYEE', 'CONSULTANT', 'VIEWER', 'HR', 'IT', 'SUPER_ADMIN'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  EMPLOYEE: 'Employee',
  CONSULTANT: 'Consultant',
  VIEWER: 'Viewer (read-only)',
  HR: 'HR',
  IT: 'IT / Asset Custodian',
  SUPER_ADMIN: 'Administrator',
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  EMPLOYEE: 'Sees only the assets issued to them, and acknowledges a handover.',
  CONSULTANT:
    'The same as an employee, for someone outside the payroll. Holds company kit, sees only what is issued to them.',
  VIEWER: 'Reads the register and the reports. Changes nothing.',
  HR: 'Owns people: onboarding, employee records, exit and clearance.',
  IT: 'Owns hardware: the register, assignments, returns and repairs.',
  SUPER_ADMIN: 'Everything, plus settings, the code sequence and user roles.',
};

/** IT owns the hardware register. */
export function canManageAssets(role: Role): boolean {
  return role === 'IT' || role === 'SUPER_ADMIN';
}

/** Issuing and taking back hardware is the same custodial job as registering it. */
export function canAssignAssets(role: Role): boolean {
  return canManageAssets(role);
}

export function canManageRepairs(role: Role): boolean {
  return canManageAssets(role);
}

/** HR owns the people records. */
export function canManageEmployees(role: Role): boolean {
  return role === 'HR' || role === 'SUPER_ADMIN';
}

/**
 * Exit clearance is HR's, even though the thing being cleared is IT's hardware.
 * IT reports whether the laptop came back; HR decides whether the person is cleared.
 */
export function canRunExitClearance(role: Role): boolean {
  return canManageEmployees(role);
}

/**
 * The whole register, rather than only what is issued to you.
 *
 * An allowlist, not `!== 'EMPLOYEE'`. The negative form read the same for years and
 * then quietly handed the entire company's hardware list to CONSULTANT the moment that
 * role was added - a role granted to people who are not on the payroll. A new role now
 * starts with no visibility and has to be named here to gain any.
 */
export function canViewRegister(role: Role): boolean {
  return role === 'VIEWER' || role === 'HR' || role === 'IT' || role === 'SUPER_ADMIN';
}

export function canViewReports(role: Role): boolean {
  return canViewRegister(role);
}

/** The asset code sequence and user roles. Narrower than either custodial role. */
export function canManageSettings(role: Role): boolean {
  return role === 'SUPER_ADMIN';
}

/**
 * Deleting is not "editing harder".
 *
 * An asset or employee with lifecycle history behind it is a record of who held
 * company property and when - the thing an audit asks for. Archiving is the normal
 * end of life for both; a hard delete is the administrator's call alone.
 */
export function canDeleteRecords(role: Role): boolean {
  return role === 'SUPER_ADMIN';
}

/**
 * Where an asset is in its life.
 *
 * ASSIGNED is stored here AND implied by an open Assignment row. The two cannot
 * drift because every transition goes through one transaction that writes both, and
 * the database carries a partial unique index allowing at most one open assignment
 * per asset - see assertAssetTransition below and the schema's
 * `assignment_one_open_per_asset` index.
 */
export const ASSET_STATUSES = [
  'AVAILABLE',
  'ASSIGNED',
  'UNDER_REPAIR',
  'LOST',
  'DAMAGED',
  'RETIRED',
  'DISPOSED',
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  AVAILABLE: 'Available',
  ASSIGNED: 'Assigned',
  UNDER_REPAIR: 'Under repair',
  LOST: 'Lost',
  DAMAGED: 'Damaged',
  RETIRED: 'Retired',
  DISPOSED: 'Disposed',
};

/**
 * RETIRED and DISPOSED are the archive. An asset in either is out of service for
 * good and disappears from every picker - which is why they are the only two states
 * the register's default view hides.
 */
export const ARCHIVED_ASSET_STATUSES: readonly AssetStatus[] = ['RETIRED', 'DISPOSED'];

export function isArchivedAsset(status: AssetStatus): boolean {
  return ARCHIVED_ASSET_STATUSES.includes(status);
}

/** Assignable right now. LOST and DAMAGED are deliberately not: they need a
 *  decision (write off, or repair) before anyone is handed them again. */
export function isAssignable(status: AssetStatus): boolean {
  return status === 'AVAILABLE';
}

/**
 * Who actually owns the thing.
 *
 * A register that only holds company kit cannot answer "what is this person using",
 * which is the question IT support and security ask - so an employee's own laptop or
 * phone is registered and issued here like anything else. What ownership changes is
 * whether losing sight of it costs the company anything.
 *
 * COMPANY is the default everywhere, including for every asset registered before this
 * column existed: the register was built for company hardware, and quietly deciding
 * an old row might be somebody's personal property would drop it out of the exit chase
 * with nobody having said so.
 */
export const ASSET_OWNERSHIPS = ['COMPANY', 'EMPLOYEE'] as const;
export type AssetOwnership = (typeof ASSET_OWNERSHIPS)[number];

export const ASSET_OWNERSHIP_LABELS: Record<AssetOwnership, string> = {
  COMPANY: 'Company-owned',
  EMPLOYEE: 'Employee-owned',
};

export const ASSET_OWNERSHIP_DESCRIPTIONS: Record<AssetOwnership, string> = {
  COMPANY: 'The company bought it and is owed it back.',
  EMPLOYEE: "The person's own device, tracked here but never owed back to us.",
};

/**
 * Whether the company is owed this asset back.
 *
 * The single predicate behind every "still outstanding" count: exit clearance refuses
 * CLEARED while company property is out, and the Monday standing report chases people
 * who left holding it. An employee-owned device fails both on purpose - requiring
 * someone to hand back their own phone before HR will mark them cleared is a demand
 * the company has no basis to make, and it is exactly the false blocker that pushes
 * HR into recording CLEARED_WITH_COMMENTS on an exit that was actually clean.
 */
export function isCompanyProperty(ownership: AssetOwnership): boolean {
  return ownership === 'COMPANY';
}

export const EMPLOYEE_STATUSES = ['ACTIVE', 'NOTICE_PERIOD', 'INACTIVE', 'EXITED'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const EMPLOYEE_STATUS_LABELS: Record<EmployeeStatus, string> = {
  ACTIVE: 'Active',
  NOTICE_PERIOD: 'Notice period',
  INACTIVE: 'Inactive',
  EXITED: 'Exited',
};

/** Condition recorded at handover and again at return - the pair is what turns
 *  "it came back scratched" into something with a date against it. */
export const ASSET_CONDITIONS = ['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'] as const;
export type AssetCondition = (typeof ASSET_CONDITIONS)[number];

export const ASSET_CONDITION_LABELS: Record<AssetCondition, string> = {
  NEW: 'New',
  GOOD: 'Good',
  FAIR: 'Fair',
  POOR: 'Poor',
  DAMAGED: 'Damaged',
};

export const REPAIR_STATUSES = ['OPEN', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
export type RepairStatus = (typeof REPAIR_STATUSES)[number];

export const REPAIR_STATUS_LABELS: Record<RepairStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export function isRepairClosed(status: RepairStatus): boolean {
  return status === 'COMPLETED' || status === 'CANCELLED';
}

/**
 * Exit clearance outcome.
 *
 * CLEARED is the only one that means "nothing outstanding", and the API refuses to
 * write it while that person still holds an asset - see assertClearanceAllowed. The
 * middle two exist because a real exit often ends with a known, accepted shortfall
 * (a lost charger recovered from final settlement) and recording that honestly beats
 * either lying with CLEARED or leaving the record PENDING forever.
 */
export const CLEARANCE_STATUSES = [
  'PENDING',
  'PARTIALLY_CLEARED',
  'CLEARED_WITH_COMMENTS',
  'CLEARED',
] as const;
export type ClearanceStatus = (typeof CLEARANCE_STATUSES)[number];

export const CLEARANCE_STATUS_LABELS: Record<ClearanceStatus, string> = {
  PENDING: 'Pending',
  PARTIALLY_CLEARED: 'Partially cleared',
  CLEARED_WITH_COMMENTS: 'Cleared with comments',
  CLEARED: 'Cleared',
};

/** Entities an uploaded file can hang off. */
export const ATTACHMENT_ENTITIES = ['ASSET', 'ASSIGNMENT', 'RETURN', 'REPAIR', 'EXIT'] as const;
export type AttachmentEntity = (typeof ATTACHMENT_ENTITIES)[number];
